/**
 * `@4f/frontend`'s error-reporting composition — the one place `@sentry/browser`
 * is imported in this SPA. WHAT LEAVES THE BOX is decided once, in
 * `packages/shared/src/observability.ts` (`shouldReportError`, `scrubEvent`,
 * `screenReportEvent`, `baseReportingOptions`); this file only turns that shared
 * policy into a concrete `Sentry.init(...)` and the {@link ErrorReporting} that
 * `main.tsx` installs before it mounts anything.
 *
 * Configuration is build-time (`VITE_GLITCHTIP_DSN`, baked into the bundle by
 * Vite), never runtime — there is no server here to read an env var from at
 * request time. An unset or blank DSN is the default for local dev and every
 * test in this repo: `Sentry.init` is never called at all.
 *
 * Neither SPA in this monorepo renders an `ErrorBoundary`, and nothing in this
 * app calls the {@link ErrorReporting.capture} returned below — `main.tsx`
 * discards `initErrorReporting()`'s return value today. That leaves
 * `globalHandlersIntegration` (an uncaught render throw or an unhandled promise
 * rejection) as the ONLY path an event takes to GlitchTip, and it reports
 * straight through the SDK client, bypassing `capture()` entirely. So
 * `beforeSend: screenReportEvent` below — not the `shouldReportError` check
 * inside `capture()` — is the sole enforcement point for both the
 * business-outcome filter and `scrubEvent`, for every event this app actually
 * sends. `capture()` stays on {@link ErrorReporting} as the shared
 * `ErrorReporter` port (DIP) for whenever this SPA grows an `ErrorBoundary`, but
 * its presence here is not evidence it runs in production today.
 */
import {
  baseReportingOptions,
  type ErrorReporter,
  noopErrorReporter,
  resolveDsn,
  screenReportEvent,
  shouldReportError,
} from '@4f/shared';
import * as Sentry from '@sentry/browser';

/** The reporter `main.tsx` installs at boot. */
export interface ErrorReporting extends ErrorReporter {
  /** `true` when a DSN was configured and the SDK actually initialised. */
  readonly enabled: boolean;
}

/**
 * The two `@sentry/browser` entry points this module calls, bundled behind one
 * defaulted parameter so a test can supply fakes and exercise the "DSN
 * configured" branch with no network call and no module mock — see
 * `test/unit/observability.test.ts`.
 */
export interface SentrySdk {
  readonly init: typeof Sentry.init;
  readonly captureException: typeof Sentry.captureException;
}

const defaultSdk: SentrySdk = { init: Sentry.init, captureException: Sentry.captureException };

/** The reporter used when no DSN is configured, or `Sentry.init` itself threw. */
function disabledReporting(): ErrorReporting {
  return { enabled: false, capture: noopErrorReporter.capture };
}

/**
 * Initialise GlitchTip reporting from the build-time env.
 *
 * `env` defaults to `import.meta.env` (Vite's build-time-replaced env object)
 * and is injectable so a test can drive both branches with no bundler in the
 * loop; it is read defensively (`?.`) because `import.meta.env` is not
 * guaranteed to exist outside a real Vite build — it does not under plain
 * `bun test`.
 *
 * A configured DSN still cannot be allowed to break the app: `main.tsx` calls
 * this before it mounts anything, so `Sentry.init` runs inside `try/catch` —
 * an error tracker that can take the boot down with it is worse than none.
 */
export function initErrorReporting(
  env?: { readonly VITE_GLITCHTIP_DSN?: string },
  sdk: SentrySdk = defaultSdk,
): ErrorReporting {
  const dsn = resolveDsn((env ?? import.meta.env)?.VITE_GLITCHTIP_DSN);
  if (!dsn) {
    return disabledReporting();
  }

  try {
    sdk.init({
      ...baseReportingOptions(dsn, import.meta.env?.MODE ?? 'production'),
      // Default integrations are OFF and the list is explicit, because two of the
      // defaults reach straight into user content: `breadcrumbs.console` turns every
      // `console.*` line into a breadcrumb, and `breadcrumbs.dom` records the clicked
      // element's text/aria-label — on this app that is a companion's name and the
      // chat surface. Navigation + network breadcrumbs are kept (they are most of a
      // report's debugging value) and their URLs are query-stripped by `scrubEvent`.
      // An explicit list also means an SDK upgrade cannot silently widen what leaves
      // the browser.
      defaultIntegrations: false,
      integrations: [
        Sentry.eventFiltersIntegration(),
        Sentry.functionToStringIntegration(),
        Sentry.dedupeIntegration(),
        // NOT `Sentry.linkedErrorsIntegration()`: it walks `Error.cause` and appends
        // each link as its own `exception.values[]` entry, bypassing `scrubEvent`'s
        // top-level scrub. `AppError` forwards `cause` and this codebase chains on
        // it constantly, so the raw upstream thing — a fetch/transport error
        // carrying a full URL with a token, a provider's own error text — would
        // ride along. See `packages/shared/src/observability.ts` for the full
        // rationale; it applies to every runtime, not just this one.
        Sentry.globalHandlersIntegration(),
        Sentry.breadcrumbsIntegration({
          console: false,
          dom: false,
          fetch: true,
          history: true,
          xhr: true,
        }),
      ],
      // No `tracesSampleRate` — deliberately ABSENT, not `0`. Leaving it unset
      // keeps the SDK's performance/OpenTelemetry machinery off entirely: this
      // app has no tracing budget, and the auto-instrumentation a sample rate
      // would switch on reaches straight into outbound request bodies.
      //
      // No `replayIntegration` either, and this one is a compliance decision,
      // not an oversight: session replay on an adults-only companion chat
      // surface would record the conversation itself — a screen recording of
      // exactly the content `scrubEvent` exists to keep out of the tracker.
      //
      // `screenReportEvent` is BOTH the business-outcome filter and the scrub
      // (see its doc comment in `@4f/shared`) — and, per the module doc comment
      // above, this is the only filter that actually runs on this app's real
      // reporting path.
      beforeSend: screenReportEvent,
    });
  } catch (error) {
    console.error('error reporting init failed — reporting disabled', error);
    return disabledReporting();
  }

  return {
    enabled: true,
    capture: (error, context) => {
      // Belt and braces, not the authoritative gate: `beforeSend: screenReportEvent`
      // above already applies `shouldReportError` to every event this app actually
      // sends (see the module doc comment — nothing here calls `capture` today).
      // Kept because it costs nothing and short-circuits before the SDK does any
      // work, for the day something in this SPA does call `capture` directly.
      if (!shouldReportError(error)) {
        return;
      }
      try {
        sdk.captureException(error, { extra: { ...context } });
      } catch (captureError) {
        console.error('error reporting capture failed', captureError);
      }
    },
  };
}
